import { Panel } from '@hover/blueprint';
import { useTheme } from '@hover/blueprint/chakra';
import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { Component as Accents } from 'src/assets/icons/nav/accents.svg';
import { Component as Doors } from 'src/assets/icons/nav/doors.svg';
import { Component as Reset } from 'src/assets/icons/nav/reset.svg';
import { Component as Roof } from 'src/assets/icons/nav/roof.svg';
import { Component as Trim } from 'src/assets/icons/nav/trim.svg';
import { Component as Versions } from 'src/assets/icons/nav/versions.svg';
import { Component as Walls } from 'src/assets/icons/nav/walls.svg';
import { Component as Windows } from 'src/assets/icons/nav/window.svg';
import { PoweredByHover } from 'src/modules/designStudio/components/DesignStudio/GuestMode/PoweredByHover';
import {
  LANDSCAPE_TOOLBAR_WIDTH,
  TOOLBAR_HEIGHT,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { usePartner } from 'src/modules/designStudio/hooks';
import { Mode, Partner } from 'src/modules/designStudio/providers';
import { useIsLandscape, useMode } from 'src/shared/hooks';

import { Menu } from '../../constants';

import { ToolbarButton } from './ToolbarButton';

const ScrollContainer = styled(Panel)`
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */

  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
`;

interface PropTypes {
  menu: Menu | null;
  setMenu: (menu: Menu | null) => void;
}

const HorizontalDivider = () => (
  <Panel
    borderTopColor="neutral200"
    borderTopStyle="solid"
    borderTopWidth="0.5px"
    height="1px"
    margin={200}
    padding={0}
    width="80%"
  />
);
const Divider = ({ isLandscape }: { isLandscape: boolean }) =>
  isLandscape ? (
    <HorizontalDivider />
  ) : (
    <Panel
      borderRightColor="neutral200"
      borderRightStyle="solid"
      borderRightWidth="0.5px"
      height="64px"
      margin={200}
      padding={0}
      width="1px"
    />
  );

const Toolbar = forwardRef<HTMLDivElement, PropTypes>(
  ({ setMenu, menu }, ref) => {
    const [isLandscape] = useIsLandscape();
    const mode = useMode();
    const partner = usePartner();
    const theme = useTheme();

    const displayVersionAndReset = mode !== Mode.GUEST;
    const displayDisabledMenuItems =
      partner === Partner.DEFAULT && mode !== Mode.GUEST;

    return (
      <Panel
        borderRadius={0}
        bottom={0}
        height={isLandscape ? '100%' : `${TOOLBAR_HEIGHT}px`}
        id="toolbar"
        left={isLandscape ? 0 : undefined}
        marginX={0}
        padding={0}
        paddingLeft="env(safe-area-inset-left, 0)"
        position="absolute"
        ref={ref}
        style={{
          pointerEvents: 'auto',
        }}
        top={isLandscape ? 0 : undefined}
        width={isLandscape ? `${LANDSCAPE_TOOLBAR_WIDTH}px` : '100%'}
      >
        <ScrollContainer
          alignContent="flex-end"
          alignItems={isLandscape ? 'center' : 'flex-start'}
          borderRadius={isLandscape ? 0 : 300}
          borderRightColor={isLandscape && 'neutral200'}
          borderRightStyle={isLandscape && 'solid'}
          borderRightWidth={isLandscape && '0.5px'}
          height="100%"
          justifyContent={isLandscape ? 'center' : undefined}
          marginX={0}
          overflowX={isLandscape ? 'hidden' : 'auto'}
          padding={0}
          width={isLandscape ? LANDSCAPE_TOOLBAR_WIDTH : undefined}
          // eslint-disable-next-line
          zIndex={theme.zIndices?.popover}
        >
          <Panel
            flexDirection={isLandscape ? 'column' : 'row'}
            overflowX={isLandscape ? 'hidden' : 'auto'}
            overflowY={isLandscape ? 'auto' : 'hidden'}
            padding={0}
            width={isLandscape ? LANDSCAPE_TOOLBAR_WIDTH : undefined}
          >
            <Panel
              alignContent="flex-end"
              borderRadius={0}
              flexDirection={isLandscape ? 'column' : 'row'}
              height={isLandscape ? undefined : 500}
              justifyContent="space-between"
              marginRight={100}
              padding={0}
            >
              <ToolbarButton
                active={menu === Menu.WALL}
                icon={Walls}
                onPress={() => setMenu(Menu.WALL)}
                text="Walls"
              />
              <ToolbarButton
                active={menu === Menu.ROOF}
                icon={Roof}
                onPress={() => setMenu(Menu.ROOF)}
                text="Roof"
              />
              <ToolbarButton
                active={menu === Menu.TRIM}
                icon={Trim}
                onPress={() => setMenu(Menu.TRIM)}
                text="Trim"
              />
              <ToolbarButton
                active={menu === Menu.WINDOW}
                icon={Windows}
                onPress={() => setMenu(Menu.WINDOW)}
                text="Windows"
              />
              {displayDisabledMenuItems && (
                <>
                  <ToolbarButton
                    active={menu === Menu.DOOR}
                    disabled
                    icon={Doors}
                    onPress={() => setMenu(Menu.DOOR)}
                    text="Doors"
                  />
                  <ToolbarButton
                    active={menu === Menu.ACCENT}
                    disabled
                    icon={Accents}
                    onPress={() => setMenu(Menu.ACCENT)}
                    text="Accents"
                  />
                </>
              )}
            </Panel>

            {displayVersionAndReset && (
              <>
                <Divider isLandscape={isLandscape} />
                <Panel
                  alignContent="flex-end"
                  flexDirection={isLandscape ? 'column' : 'row'}
                  height={isLandscape ? undefined : 500}
                  justifyContent="space-between"
                  marginX={0}
                  padding={0}
                >
                  <ToolbarButton
                    active={menu === Menu.VERSION}
                    disabled
                    icon={Versions}
                    onPress={() => setMenu(Menu.VERSION)}
                    text="Version"
                  />
                  <ToolbarButton
                    active={menu === Menu.RESET}
                    icon={Reset}
                    onPress={() => setMenu(Menu.RESET)}
                    text="Reset"
                  />
                </Panel>
              </>
            )}
          </Panel>
        </ScrollContainer>
        <PoweredByHover />
      </Panel>
    );
  },
);

export { Toolbar, TOOLBAR_HEIGHT };
